import React from 'react';

import AgentListItemTitleMeta from 'llm/chat-library/agents/AgentListItemTitleMeta';
import type {ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {canCurrentUserModifyRoutine} from 'llm/routines/routineAuthorization';
import {
  EventTriggerConfig,
  Routine,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from 'llm/routines/RoutineTypes';
import {formatEventTrigger, formatTimeTrigger} from 'llm/routines/RoutineUtils';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import type {HasAvatar} from 'ui-library/avatar/Avatar';
import type {MantineOverflowMenuOption} from 'ui-library/menus/MantineOverflowMenu';
import * as Logging from 'util/Logging';

const UNTITLED_AGENT_NAME = 'Untitled Agent';

function formatAgentListSubtitle(creator: HasAvatar | null | undefined, trailingText: string): string {
  const creatorLabel = creator != null ? getPartyDisplayString(creator) : undefined;
  if (creatorLabel == null || creatorLabel === '') {
    return trailingText;
  }
  return `Created by ${creatorLabel} · ${trailingText}`;
}

export default function buildListItemViewModelFromAgent(
  routine: Routine,
  handlers: {
    onToggleActive: (routine: Routine, active: boolean) => Promise<void>;
    onRun: (routine: Routine) => Promise<void>;
    onArchive: (routine: Routine) => void;
    onViewDetails: (routine: Routine) => void;
  }
): ListItemViewModel {
  const isTimeBased = routine.triggerType === TriggerType.TIME_BASED;
  const displayName = routine.name ?? UNTITLED_AGENT_NAME;
  // PENDING_REGISTRATION is treated as active (matches the legacy listing), so the toggle is only
  // off when the agent is explicitly paused.
  const isActive = routine.status !== RoutineStatus.PAUSED;
  const canWrite = routine.permissions.writable && routine.archived !== true;
  const action = isActive ? 'pause' : 'resume';
  const canChangeStatus = canWrite && canCurrentUserModifyRoutine(routine, action);
  const triggerText = isTimeBased
    ? formatTimeTrigger(routine.triggerConfig as TimeTriggerConfig)
    : formatEventTrigger(routine.triggerConfig as EventTriggerConfig);

  const actionItems: Array<MantineOverflowMenuOption> = [];
  if (isTimeBased) {
    const canRun = routine.permissions.appendable && canCurrentUserModifyRoutine(routine, 'run');
    actionItems.push({
      name: 'Run now',
      isDisabled: !canRun,
      icon: canRun ? undefined : 'ban',
      disabledTooltip: canRun ? undefined : 'You are not authorized to run the agent.',
      onSelect: () => {
        // The overflow menu's onSelect is synchronous and can't propagate a rejection to a global
        // handler. `onRun` surfaces known server errors (e.g. the execution rate limit) as a toast
        // and re-throws only unexpected errors, so report those here instead of silently
        // discarding them.
        handlers.onRun(routine).catch((error: unknown) => {
          Logging.error(error instanceof Error ? error : new Error(String(error)), {
            extra: {routineId: routine.id},
          });
        });
      },
    });
  }
  if (routine.archived !== true && routine.permissions.archivable === true) {
    actionItems.push({
      name: 'Archive',
      variant: 'destructive',
      onSelect: () => {
        handlers.onArchive(routine);
      },
    });
  }

  return {
    id: routine.id,
    title: displayName,
    titleBadge: <AgentListItemTitleMeta creator={routine.creator} runAs={routine.runAs} />,
    subtitle: formatAgentListSubtitle(routine.creator, triggerText),
    description: routine.prompt,
    toggle: {
      checked: isActive,
      ariaLabel: isActive ? `Pause ${displayName}` : `Resume ${displayName}`,
      tooltip: isActive ? 'Pause agent' : 'Resume agent',
      onChange: (active) => handlers.onToggleActive(routine, active),
      disabled: !canChangeStatus,
    },
    menu: [
      {items: actionItems},
      // Always include View details so every agent row shows the overflow menu, even when the
      // action section above is empty.
      {
        items: [
          {
            name: 'View details',
            onSelect: () => {
              handlers.onViewDetails(routine);
            },
          },
        ],
      },
    ].filter((menuSection) => menuSection.items.length > 0),
  };
}
